/**
 * Format de trame du canal de données WebRTC.
 *
 * Chaque message applicatif — contrôle ou morceau de fichier — est une trame binaire :
 *
 * ```text
 *  0        1              5              9                     21
 *  ┌────────┬──────────────┬──────────────┬─────────────────────┬──────────────────┐
 *  │ type   │ fileIndex    │ seq          │ iv (12 octets)      │ payload scellé   │
 *  │ 1 octet│ 4 octets BE  │ 4 octets BE  │                     │ + tag GCM (16 o) │
 *  └────────┴──────────────┴──────────────┴─────────────────────┴──────────────────┘
 * ```
 *
 * Le triplet `(type, fileIndex, seq)` sert de **données authentifiées additionnelles** (AAD)
 * au chiffrement AES-GCM. Conséquence : réordonner, dupliquer ou rejouer une trame invalide
 * le tag d'authentification et la trame est rejetée. L'index n'est donc pas qu'un confort
 * de lecture — c'est la protection contre le rejeu.
 *
 * **Le type fait partie de l'AAD.** Sans lui, une trame de contrôle et une trame de données
 * portant le même couple `(fileIndex, seq)` partageraient les mêmes données authentifiées :
 * un pair malveillant pourrait présenter un message de contrôle comme un morceau de fichier,
 * ou l'inverse. Séparer les domaines par le type rend cette confusion impossible.
 *
 * Le module est volontairement pur : aucune dépendance, testable isolément.
 */

import { FRAME_HEADER_SIZE, IV_BYTES } from './constants.js';
import type { Bytes } from './types.js';

export interface Frame {
  type: number;
  fileIndex: number;
  seq: number;
  iv: Bytes;
  payload: Bytes;
}

/** Sérialise une trame en un unique `Uint8Array` prêt à passer à `RTCDataChannel.send`. */
export function encodeFrame(frame: Frame): Bytes {
  if (frame.iv.byteLength !== IV_BYTES) {
    throw new Error(`IV de taille invalide : ${frame.iv.byteLength} (attendu ${IV_BYTES})`);
  }
  const out = new Uint8Array(FRAME_HEADER_SIZE + frame.payload.byteLength);
  const view = new DataView(out.buffer);
  out[0] = frame.type;
  view.setUint32(1, frame.fileIndex, false);
  view.setUint32(5, frame.seq, false);
  out.set(frame.iv, 9);
  out.set(frame.payload, FRAME_HEADER_SIZE);
  return out;
}

/**
 * Désérialise une trame reçue.
 *
 * Lève une erreur si la trame est trop courte : un pair malveillant ou un bug de framing
 * ne doit pas produire un `undefined` silencieux propagé plus loin dans le déchiffrement.
 */
export function decodeFrame(input: ArrayBuffer | Bytes): Frame {
  const bytes: Bytes = input instanceof Uint8Array ? input : new Uint8Array(input);
  if (bytes.byteLength < FRAME_HEADER_SIZE) {
    throw new Error(`Trame trop courte : ${bytes.byteLength} octets`);
  }
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  return {
    type: bytes[0] as number,
    fileIndex: view.getUint32(1, false),
    seq: view.getUint32(5, false),
    iv: bytes.slice(9, 9 + IV_BYTES),
    payload: bytes.slice(FRAME_HEADER_SIZE),
  };
}

/**
 * Construit les données authentifiées additionnelles pour une trame donnée.
 *
 * Format : `[1 octet type][4 octets fileIndex][4 octets seq]`, en big-endian.
 *
 * Lier le type, l'index de fichier et le numéro de séquence garantit qu'une trame ne peut
 * pas être déplacée : ni d'un fichier à un autre, ni d'une position à une autre, ni d'un
 * type de trame à l'autre.
 */
export function frameAad(type: number, fileIndex: number, seq: number): Bytes {
  const aad = new Uint8Array(9);
  const view = new DataView(aad.buffer);
  aad[0] = type;
  view.setUint32(1, fileIndex, false);
  view.setUint32(5, seq, false);
  return aad;
}
